import React, { CSSProperties } from "react";
import { Select, FormControl, Checkbox, useTheme } from "@mui/material";
import { styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { StyledMenuItem, StyledItemSelect } from "./select-atoms";

/**
 * @deprecated
 * Component which simplifies selections. Enforces that each option has a label and an ID to allow for easier decoupling of the two.
 */
export function ConciseSelector<T extends string | number, Multi extends boolean = false, SelectionType = Multi extends true ? T[] : T>({
    options,
    onChange,
    values,
    multi,
    className,
    renderLabel = (selected: SelectionType) => {
        if (Array.isArray(selected)) {
            if (selected.length === 0) {
                return "None selected";
            }

            if (selected.length === options.length) {
                return "All selected";
            }

            return selected.map((selectedValue: T) => options.find(({ id }) => id === selectedValue).label).join(", ");
        }
        return selected.toString();
    },
    disabled,
    toggleAllOptions = false,
    toggleAllOptionsLabel,
    formControlStyle = {},
    dataTestId,
}: React.PropsWithChildren<{
    options: { label: string; id: T }[];
    onChange: (selection: SelectionType) => void;
    values: SelectionType;
    multi: Multi;
    className?: string;
    renderLabel?: (selection: SelectionType) => string | React.ReactNode;
    disabled?: boolean;
    toggleAllOptions?: boolean;
    toggleAllOptionsLabel?: string;
    formControlStyle?: CSSProperties;
    dataTestId?: string;
}>) {
    const theme = useTheme<Theme>();

    if (toggleAllOptions && !Array.isArray(values)) {
        throw new Error("toggleAllOptions can't be set to true unless multiple values can be selected");
    }

    return (
        <>
            <FormControl variant="outlined" className={className} style={formControlStyle}>
                <Select
                    data-testid={dataTestId}
                    data-cy={dataTestId}
                    MenuProps={{
                        anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                        },
                        transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                        },
                        autoFocus: false,
                    }}
                    disabled={disabled}
                    input={<StyledItemSelect $verticalPadding="8px" />}
                    multiple={multi}
                    value={values}
                    displayEmpty
                    renderValue={(val) =>
                        typeof renderLabel(val) === "string" ? (
                            // Prevent ugly overlapping select labels when label renderer is returning just a string
                            <span style={{ width: "100%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{renderLabel(val)}</span>
                        ) : (
                            renderLabel(val)
                        )
                    }
                    onChange={(e) => onChange(e.target.value as SelectionType)}
                >
                    {toggleAllOptions && Array.isArray(values) && (
                        <StyledMenuItem value={allOptionsValueLiteral} style={{ backgroundColor: theme.typographyColors.blank }}>
                            <Checkbox
                                style={{ padding: "0px 10px 0px 0px" }}
                                checked={values.length === options.length}
                                indeterminate={values.length !== options.length && values.length > 0}
                            />
                            <LabelStyleOverride bold={values.length === options.length || (values.length !== options.length && values.length > 0)}>
                                {toggleAllOptionsLabel || ""}
                            </LabelStyleOverride>
                        </StyledMenuItem>
                    )}
                    {options.map(({ label, id }) => (
                        <StyledMenuItem data-cy={label?.split(" ")[0]} value={id} key={id} style={{ backgroundColor: theme.typographyColors.blank }}>
                            {Array.isArray(values) && (
                                <Checkbox data-testid={id} style={{ padding: "0px 10px 0px 0px" }} className={className} checked={values.some((value) => value === id)} />
                            )}
                            {label}
                        </StyledMenuItem>
                    ))}
                </Select>
            </FormControl>
        </>
    );
}

const LabelStyleOverride = styled.div<{ bold: boolean }>`
    ${(props) =>
        props.bold &&
        `
            font-weight: 500;
        `}
`;

export const allOptionsValueLiteral = "all-options-selected-literal";
